import * as tus from 'tus-js-client';
import { supabase } from '../lib/supabase';

const DISH_IMAGES_BUCKET = 'dish-images';
const AR_MODELS_BUCKET = 'ar-models';
const RESUMABLE_UPLOAD_THRESHOLD = 6 * 1024 * 1024; // Supabase recommends resumable uploads above 6 MB
const RESUMABLE_CHUNK_SIZE = 6 * 1024 * 1024;
const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

const getResumableUploadEndpoint = () => {
  const fallbackEndpoint = `${supabaseUrl}/storage/v1/upload/resumable`;

  try {
    const parsed = new URL(supabaseUrl);
    if (parsed.hostname.endsWith('.supabase.co')) {
      const storageHost = parsed.hostname.replace(/\.supabase\.co$/, '.storage.supabase.co');
      return `${parsed.protocol}//${storageHost}/storage/v1/upload/resumable`;
    }
    return `${parsed.origin}/storage/v1/upload/resumable`;
  } catch {
    return fallbackEndpoint;
  }
};

const uploadARModelResumable = async ({ fileName, fileBlob, contentType }) => {
  const endpoint = getResumableUploadEndpoint();

  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(fileBlob, {
      endpoint,
      chunkSize: RESUMABLE_CHUNK_SIZE,
      retryDelays: [0, 1000, 3000, 5000],
      removeFingerprintOnSuccess: true,
      headers: {
        authorization: `Bearer ${supabaseAnonKey}`,
        apikey: supabaseAnonKey,
        'x-upsert': 'false',
      },
      metadata: {
        bucketName: AR_MODELS_BUCKET,
        objectName: fileName,
        contentType,
        cacheControl: '3600',
      },
      onError: (err) => reject(err),
      onSuccess: () => resolve(upload.url),
    });

    upload.findPreviousUploads().then((previousUploads) => {
      if (previousUploads.length > 0) {
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }
      upload.start();
    }).catch((err) => reject(err));
  });
};

// ---------------------------------------------------------------------------
// GLB / GLTF Diagnostic Utilities
// ---------------------------------------------------------------------------

/**
 * Diagnose a GLB binary buffer.
 * Returns a structured report describing the file's internal structure,
 * detected KIRI Engine export mode, and any issues found.
 *
 * GLB binary layout (glTF 2.0 spec):
 *   Bytes 0-3:  magic  = 0x46546C67 ("glTF")
 *   Bytes 4-7:  version (uint32 LE) — must be 2
 *   Bytes 8-11: total length (uint32 LE)
 *   Chunk 0:    JSON chunk  (type 0x4E4F534A = "JSON")
 *   Chunk 1+:   BIN chunk   (type 0x004E4942 = "BIN\0") — optional
 */
export const diagnoseGLBBuffer = (arrayBuffer) => {
  const report = {
    isGLB: false,
    isGLTF: false,
    version: null,
    totalLength: null,
    chunks: [],
    jsonContent: null,
    detectedExportMode: 'unknown',
    issues: [],
    supported: false,
    contentType: 'application/octet-stream',
  };

  try {
    const bytes = new Uint8Array(arrayBuffer);
    const view = new DataView(arrayBuffer);

    if (arrayBuffer.byteLength < 12) {
      report.issues.push('File too small to be a valid GLB (< 12 bytes)');
      return report;
    }

    // Check magic bytes
    const magic = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);

    if (magic === 'glTF') {
      report.isGLB = true;
      report.version = view.getUint32(4, true);
      report.totalLength = view.getUint32(8, true);
      report.contentType = 'model/gltf-binary';

      if (report.version !== 2) {
        report.issues.push(`GLB version ${report.version} detected — only version 2 is supported`);
      }

      if (report.totalLength !== arrayBuffer.byteLength) {
        report.issues.push(
          `GLB header declares ${report.totalLength} bytes but file is ${arrayBuffer.byteLength} bytes — file may be truncated or corrupt`
        );
      }

      // Parse chunks
      let offset = 12;
      while (offset + 8 <= arrayBuffer.byteLength) {
        const chunkLength = view.getUint32(offset, true);
        const chunkType = view.getUint32(offset + 4, true);
        const chunkTypeStr =
          chunkType === 0x4e4f534a ? 'JSON' :
          chunkType === 0x004e4942 ? 'BIN' :
          `0x${chunkType.toString(16).toUpperCase()}`;

        report.chunks.push({ offset, chunkLength, chunkType: chunkTypeStr });

        // Parse JSON chunk to detect export mode
        if (chunkTypeStr === 'JSON' && chunkLength > 0) {
          try {
            const jsonBytes = new Uint8Array(arrayBuffer, offset + 8, chunkLength);
            const jsonStr = new TextDecoder().decode(jsonBytes);
            const json = JSON.parse(jsonStr);
            report.jsonContent = {
              generator: json?.asset?.generator || null,
              extensionsUsed: json?.extensionsUsed || [],
              extensionsRequired: json?.extensionsRequired || [],
              meshCount: json?.meshes?.length || 0,
              materialCount: json?.materials?.length || 0,
              textureCount: json?.textures?.length || 0,
              nodeCount: json?.nodes?.length || 0,
              animationCount: json?.animations?.length || 0,
            };

            // Detect KIRI Engine export mode from generator string and extensions
            const gen = (json?.asset?.generator || '').toLowerCase();
            const exts = [
              ...(json?.extensionsUsed || []),
              ...(json?.extensionsRequired || []),
            ].map(e => e.toLowerCase());

            if (gen.includes('kiri')) {
              if (exts.some(e => e.includes('gaussian') || e.includes('splat') || e.includes('3dgs'))) {
                report.detectedExportMode = 'KIRI_3DGS_EMBEDDED';
              } else if (json?.meshes?.length > 0) {
                report.detectedExportMode = 'KIRI_PHOTOGRAMMETRY_MESH';
              } else {
                report.detectedExportMode = 'KIRI_UNKNOWN';
              }
            } else if (exts.some(e => e.includes('gaussian') || e.includes('splat'))) {
              report.detectedExportMode = '3DGS_EMBEDDED_GLB';
            } else if (gen.includes('polycam') || gen.includes('luma') || gen.includes('reality')) {
              report.detectedExportMode = 'THIRD_PARTY_PHOTOGRAMMETRY';
            } else if (json?.meshes?.length > 0) {
              report.detectedExportMode = 'STANDARD_MESH_GLB';
            } else {
              report.detectedExportMode = 'UNKNOWN_GLB';
            }

            // Check for unsupported extensions
            const unsupportedExts = (json?.extensionsRequired || []).filter(e =>
              !['KHR_materials_unlit', 'KHR_texture_transform', 'KHR_mesh_quantization',
                'KHR_draco_mesh_compression', 'EXT_mesh_gpu_instancing'].includes(e)
            );
            if (unsupportedExts.length > 0) {
              report.issues.push(`Required extensions may not render in all viewers: ${unsupportedExts.join(', ')}`);
            }
          } catch (jsonErr) {
            report.issues.push(`JSON chunk parse error: ${jsonErr.message}`);
          }
        }

        offset += 8 + chunkLength;
        // Chunks are 4-byte aligned
        if (chunkLength % 4 !== 0) offset += 4 - (chunkLength % 4);
      }

      const hasJSON = report.chunks.some(c => c.chunkType === 'JSON');
      const hasBIN = report.chunks.some(c => c.chunkType === 'BIN');

      if (!hasJSON) report.issues.push('Missing JSON chunk — file may be corrupt');
      if (!hasBIN && report.jsonContent?.meshCount > 0) {
        report.issues.push('No BIN chunk found despite mesh data — geometry may be missing or embedded as base64');
      }

      report.supported = report.issues.filter(i => i.includes('corrupt') || i.includes('truncated')).length === 0;

    } else {
      // Not a GLB — check if it might be a plain-text GLTF JSON
      try {
        const text = new TextDecoder().decode(bytes.slice(0, Math.min(512, bytes.length)));
        if (text.trimStart().startsWith('{') && text.includes('"asset"')) {
          report.isGLTF = true;
          report.detectedExportMode = 'GLTF_JSON';
          report.contentType = 'model/gltf+json';
          report.supported = true;
        } else if (bytes[0] === 0x73 && bytes[1] === 0x70 && bytes[2] === 0x6c && bytes[3] === 0x61) {
          // "spla" — likely a .splat file
          report.detectedExportMode = 'GAUSSIAN_SPLAT';
          report.contentType = 'application/octet-stream';
          report.issues.push('Raw Gaussian Splat (.splat) files cannot be rendered in standard WebXR viewers — export as GLB/Mesh from KIRI Engine instead');
          report.supported = false;
        } else {
          report.issues.push(`Unknown file format — magic bytes: ${bytes.slice(0, 4).join(' ')}`);
          report.supported = false;
        }
      } catch {
        report.issues.push('Could not read file header');
        report.supported = false;
      }
    }
  } catch (err) {
    report.issues.push(`Diagnostic error: ${err.message}`);
  }

  return report;
};

/**
 * Upload a dish image file to Supabase Storage.
 * Returns the public URL of the uploaded file.
 */
export const uploadDishImage = async (file) => {
  const ext = file?.name?.split('.')?.pop();
  const fileName = `dish-${Date.now()}-${Math.random()?.toString(36)?.slice(2)}.${ext}`;

  const { error } = await supabase?.storage?.from(DISH_IMAGES_BUCKET)?.upload(fileName, file, { cacheControl: '3600', upsert: false });

  if (error) throw error;

  const { data } = supabase?.storage?.from(DISH_IMAGES_BUCKET)?.getPublicUrl(fileName);

  return data?.publicUrl;
};

/**
 * Upload an AR model file to Supabase Storage.
 * Returns { url, diagnostics } where diagnostics is the GLB report.
 *
 * Supports all KIRI Engine export modes:
 *   - Photogrammetry → GLB (standard mesh)
 *   - 3DGS → Mesh → GLB (Gaussian Splat baked to mesh)
 *   - NeRF → Mesh → GLB
 *   - GLTF JSON + external BIN (uploaded as-is)
 *   - USDZ (Apple AR)
 *   - PLY point clouds
 *
 * NOT supported (raw splat files):
 *   - .splat — raw Gaussian Splat, no WebXR renderer support
 */
export const uploadARModel = async (file) => {
  const ext = file?.name?.split('.')?.pop()?.toLowerCase();
  const fileName = `ar-${Date.now()}-${Math.random()?.toString(36)?.slice(2)}.${ext}`;

  // Read the file buffer — needed for diagnostics, magic-byte detection, and upload
  const arrayBuffer = await file?.arrayBuffer();

  // Run diagnostics first
  const diagnostics = diagnoseGLBBuffer(arrayBuffer);

  // Log diagnostics to console for developer visibility
  console.group(`[AR Upload] GLB Diagnostics — ${file.name}`);
  console.log('Export mode detected:', diagnostics.detectedExportMode);
  console.log('File size:', `${(file.size / 1024 / 1024).toFixed(2)} MB`);
  if (diagnostics.isGLB) {
    console.log('GLB version:', diagnostics.version);
    console.log('Chunks:', diagnostics.chunks.map(c => `${c.chunkType}(${c.chunkLength}b)`).join(', '));
    if (diagnostics.jsonContent) {
      console.log('Generator:', diagnostics.jsonContent.generator || 'unknown');
      console.log('Meshes:', diagnostics.jsonContent.meshCount, '| Materials:', diagnostics.jsonContent.materialCount, '| Textures:', diagnostics.jsonContent.textureCount);
      if (diagnostics.jsonContent.extensionsUsed.length > 0) {
        console.log('Extensions used:', diagnostics.jsonContent.extensionsUsed.join(', '));
      }
      if (diagnostics.jsonContent.extensionsRequired.length > 0) {
        console.log('Extensions required:', diagnostics.jsonContent.extensionsRequired.join(', '));
      }
    }
  }
  if (diagnostics.issues.length > 0) {
    console.warn('Issues found:', diagnostics.issues);
  }
  console.log('Supported:', diagnostics.supported);
  console.groupEnd();

  // Block unsupported formats with a descriptive error
  if (!diagnostics.supported && diagnostics.detectedExportMode === 'GAUSSIAN_SPLAT') {
    const err = new Error(
      'Raw .splat files are not supported by WebXR viewers. ' + 'In KIRI Engine, use "Export → GLB" with the "Mesh" output mode instead of "Splat".'
    );
    err.diagnostics = diagnostics;
    throw err;
  }

  // Determine content type from diagnostics (most accurate) or fall back to extension map
  let contentType = diagnostics.contentType;

  if (contentType === 'application/octet-stream') {
    const mimeMap = {
      glb: 'model/gltf-binary',
      gltf: 'model/gltf+json',
      usdz: 'model/vnd.usdz+zip',
      ply: 'application/octet-stream',
      splat: 'application/octet-stream',
    };
    contentType = mimeMap?.[ext] || 'application/octet-stream';
  }

  // Re-wrap as a Blob with the resolved MIME type so Supabase validates correctly.
  const typedBlob = new Blob([arrayBuffer], { type: contentType });

  try {
    if (typedBlob.size > RESUMABLE_UPLOAD_THRESHOLD) {
      console.log(`[AR Upload] Using resumable upload (TUS) for ${fileName}`);
      await uploadARModelResumable({ fileName, fileBlob: typedBlob, contentType });
    } else {
      console.log(`[AR Upload] Using standard upload for ${fileName}`);
      const { error } = await supabase?.storage?.from(AR_MODELS_BUCKET)?.upload(fileName, typedBlob, {
        cacheControl: '3600',
        upsert: false,
        contentType,
      });

      if (error) throw error;
    }
  } catch (error) {
    const uploadErr = new Error(error?.message || 'Upload failed');
    uploadErr.diagnostics = diagnostics;
    uploadErr.supabaseError = error;
    throw uploadErr;
  }

  const { data } = supabase?.storage?.from(AR_MODELS_BUCKET)?.getPublicUrl(fileName);

  return { url: data?.publicUrl, diagnostics };
};

/**
 * Upload a promotion image file to Supabase Storage.
 * Returns the public URL of the uploaded file.
 */
export const uploadPromotionImage = async (file) => {
  const ext = file?.name?.split('.')?.pop();
  const fileName = `promo-${Date.now()}-${Math.random()?.toString(36)?.slice(2)}.${ext}`;

  const { error } = await supabase?.storage?.from(DISH_IMAGES_BUCKET)?.upload(fileName, file, { cacheControl: '3600', upsert: false });

  if (error) throw error;

  const { data } = supabase?.storage?.from(DISH_IMAGES_BUCKET)?.getPublicUrl(fileName);

  return data?.publicUrl;
};

/**
 * Upload a restaurant logo file to Supabase Storage.
 * Returns the public URL of the uploaded file.
 */
export const uploadRestaurantLogo = async (file) => {
  if (!supabase) throw new Error('Supabase non configuré. Veuillez configurer vos identifiants Supabase.');

  const ext = file?.name?.split('.')?.pop();
  const fileName = `restaurant-logo-${Date.now()}-${Math.random()?.toString(36)?.slice(2)}.${ext}`;

  const { error } = await supabase.storage.from(DISH_IMAGES_BUCKET).upload(fileName, file, { cacheControl: '3600', upsert: false });

  if (error) throw error;

  const { data } = supabase.storage.from(DISH_IMAGES_BUCKET).getPublicUrl(fileName);

  return data?.publicUrl;
};
